import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Loader2, Upload, RotateCcw } from 'lucide-react';
import { CameraCapture } from './CameraCapture';
import type { ActPhotoType, IdPhotoSide } from './types';
import { ACT_PHOTO_TYPE_LABELS, ID_PHOTO_SIDE_LABELS } from './types';
import { SensitiveDataBanner } from '../SensitiveDataBanner';

// ---------------------------------------------------------------------------
// Act Photo Capture Modal
// ---------------------------------------------------------------------------

interface ActPhotoCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the blob and chosen type; should return a Promise that resolves when upload is done */
  onSave: (blob: Blob, type: ActPhotoType) => Promise<void>;
  /** Pre-select a photo type (optional) */
  defaultType?: ActPhotoType;
  /** Restrict which types are shown (optional) */
  allowedTypes?: ActPhotoType[];
}

const ALL_TYPES: ActPhotoType[] = [
  'RESULT',
  'LABEL',
  'DESIGN_REF',
  'SKIN_CHECK',
  'BEFORE',
  'HEALING',
  'CONSENT_FORM',
  'JEWELRY',
  'CLIENT_ID',
];

export function ActPhotoCaptureModal({
  isOpen,
  onClose,
  onSave,
  defaultType = 'RESULT',
  allowedTypes,
}: ActPhotoCaptureModalProps) {
  const [selectedType, setSelectedType] = useState<ActPhotoType>(defaultType);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const visibleTypes = allowedTypes ?? ALL_TYPES;

  const handleCapture = (blob: Blob) => {
    setPendingBlob(blob);
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleCapture(file);
    }
    e.target.value = '';
  };

  const handleClearPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPendingBlob(null);
  };

  const handleSave = async () => {
    if (!pendingBlob) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(pendingBlob, selectedType);
      handleClearPreview();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Erreur lors de l'enregistrement de la photo");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    handleClearPreview();
    setError(null);
    onClose();
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const modal = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 pt-5 pb-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 bg-primary-100 rounded-full">
                  <Camera className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Prendre une photo</h3>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Type selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de photo
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as ActPhotoType)}
                className="input-field"
                disabled={saving}
              >
                {visibleTypes.map((t) => (
                  <option key={t} value={t}>
                    {ACT_PHOTO_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            {selectedType === 'CLIENT_ID' && (
              <SensitiveDataBanner
                message="Photo d'identité client : donnée sensible chiffrée. Vérifiez l'identité en présence du client et ne conservez que les clichés nécessaires au dossier."
                className="mb-4"
              />
            )}

            {previewUrl ? (
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-full max-w-md rounded-xl overflow-hidden bg-gray-900 aspect-video flex items-center justify-center">
                  <img src={previewUrl} alt="Aperçu" className="w-full h-full object-contain" />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleClearPreview}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Changer de photo
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <CameraCapture onCapture={handleCapture} autoStart />
                <div className="flex items-center justify-center">
                  <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">ou</span>
                </div>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Upload className="w-4 h-4 text-gray-500" />
                    Importer un fichier
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500">
              {pendingBlob
                ? 'Photo prête — cliquez sur Enregistrer pour la sauvegarder.'
                : 'Prenez une photo puis confirmez avant d\'enregistrer.'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!pendingBlob || saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ---------------------------------------------------------------------------
// ID Photo Capture Modal (front / back — single slot)
// ---------------------------------------------------------------------------

interface IdPhotoCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (blob: Blob, side: IdPhotoSide) => Promise<void>;
  defaultSide?: IdPhotoSide;
}

export function IdPhotoCaptureModal({
  isOpen,
  onClose,
  onSave,
  defaultSide = 'front',
}: IdPhotoCaptureModalProps) {
  const [selectedSide, setSelectedSide] = useState<IdPhotoSide>(defaultSide);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync the selected side whenever the modal opens or the requested side changes
  useEffect(() => {
    if (isOpen) {
      setSelectedSide(defaultSide);
    }
  }, [isOpen, defaultSide]);

  const handleCapture = (blob: Blob) => {
    setPendingBlob(blob);
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleCapture(file);
    }
    e.target.value = '';
  };

  const handleClearPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPendingBlob(null);
  };

  const handleSave = async () => {
    if (!pendingBlob) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(pendingBlob, selectedSide);
      handleClearPreview();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Erreur lors de l'enregistrement de la photo");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    handleClearPreview();
    setError(null);
    onClose();
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const modal = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 pt-5 pb-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 bg-blue-100 rounded-full">
                  <Camera className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Photo de pièce d'identité</h3>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <SensitiveDataBanner
              message="Capture de pièce d'identité : image chiffrée à l'enregistrement. Photographiez uniquement le document du client présent, recto/verso si requis."
              className="mb-4"
            />

            {/* Side selector */}
            <div className="mb-4 flex gap-2">
              {(['front', 'back'] as const).map((side) => (
                <button
                  key={side}
                  type="button"
                  onClick={() => setSelectedSide(side)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    selectedSide === side
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {ID_PHOTO_SIDE_LABELS[side]}
                </button>
              ))}
            </div>

            {previewUrl ? (
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-full max-w-md rounded-xl overflow-hidden bg-gray-900 aspect-video flex items-center justify-center">
                  <img src={previewUrl} alt="Aperçu" className="w-full h-full object-contain" />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleClearPreview}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Changer de photo
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <CameraCapture onCapture={handleCapture} autoStart />
                <div className="flex items-center justify-center">
                  <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">ou</span>
                </div>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Upload className="w-4 h-4 text-gray-500" />
                    Importer un fichier
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500">
              {pendingBlob
                ? `${ID_PHOTO_SIDE_LABELS[selectedSide]} prêt — cliquez sur Enregistrer.`
                : 'Prenez une photo puis confirmez avant d\'enregistrer.'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!pendingBlob || saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
